"use client";

import {
  useGetCompany,
  useGetCompanyDetailsId,
  useGetUserKycList,
  useGetUserProfileInfo,
  useUpdateUserDoc,
  useUpdateUserProfilePhoto,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Camera, IdCard, MapPin, Shield, UploadCloud } from "lucide-react";
import z from "zod";
import KYCListTable from "./KYCListTable";
import ProfileKYCForm from "./ProfileKYCForm";
import ImageEditor from "./ImageEditor";

export const schema = z.object({
  kyc_type: z.string().min(1, { message: "KYC type is required" }),

  kyc: z.string().min(1, { message: "KYC number is required" }),
  pan: z.string().optional(),
  gst: z.string().optional(),
  address: z.string().optional(),
  pincode: z.string().optional(),
  msme: z.string().optional(),
  license_state: z.string().optional(),
  license_validity: z.string().optional(),
  license_no: z.string().optional(),
  gps_device_no: z.string().optional(),
  rc: z.string().optional(),

  last_updated_on: z
    .string()
    .optional()
    .or(z.literal("")) // Allows empty string if no date is selected
    .refine((val) => !val || !isNaN(new Date(val).getTime()), {
      message: "Invalid date format",
    }),

  verified_by: z.string().optional().or(z.literal("")), // Allows empty string
});

export type FormData = z.infer<typeof schema>;

export default function UserKYCDetailForm() {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });
  const onSubmit = async (data: any) => {};
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [panProof, setPanProof] = useState<null | string>(null);
  const [gstProof, setGstProof] = useState<null | string>(null);
  const [msmeProof, setMsmeProof] = useState<null | string>(null);
  const [addressProof, setAddressProof] = useState<null | string>(null);
  const [companyFrontImage, setCompanyFrontImage] = useState<string | null>(
    null
  );
  const [companyInsideImage, setCompanyInsideImage] = useState<string | null>(
    null
  );
  const [companyTeamImage, setCompanyTeamImage] = useState<string | null>(null);

  const selectedKycType = watch("kyc_type");

  const searchParams = useSearchParams();

  const user_id = searchParams.get("user_id");

  const path = searchParams.get("type");

  const { user: authUser } = useAuth();

  const userId = path && user_id ? Number(user_id) : (authUser?.id ?? null);
  const validUserId = userId && userId > 0 ? userId : null;

  const { data: kycData, isLoading } = useGetUserKycList("kyc_proof");
  const { mutate: uploadDoc } = useUpdateUserDoc(validUserId ?? -1);
  const { mutate: uploadPhoto } = useUpdateUserProfilePhoto(validUserId ?? -1);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const {
    data: userProfile,
    isLoading: isProfileLoading,
    isError,
    error,
  } = useGetUserProfileInfo(validUserId ?? -1);
  const { data: company } = useGetCompany({ user_id: validUserId ?? -1 });
  const profilePath = process.env.NEXT_PUBLIC_API_PIC_URL;
  const b2bUrl = process.env.NEXT_PUBLIC_B2B_URL;

  useEffect(() => {
    if (userProfile) {
      reset({
        kyc_type: userProfile.kyc_type?.toString() || "",
        kyc: userProfile.kyc || "",
      });
    }
  }, [userProfile, reset]);

  useEffect(() => {
    if (company) {
      setValue("pan", company[0]?.pancard_no || "");
      setValue("gst", company[0]?.service_tax_gst || "");
      setValue("msme", company[0]?.msme_number || "");
      setValue("address", company[0]?.company_address || "");
      setValue("pincode", company[0]?.pincode?.toString() || "");
      setValue("license_no", company[0]?.license_no || "");
      setValue("license_state", company[0]?.license_state || "");
      setValue("license_validity", company[0]?.license_validity || "");
      setValue("gps_device_no", company[0]?.gps_device_no || "");
      setValue("rc", company[0]?.rc_number || "");
      setAddressProof(`${company?.images?.["Office Address"]}`);
      setMsmeProof(`${company?.images?.["MSME"]}`);
      setGstProof(`${company?.images?.["Gst Proof"]}`);
      setPanProof(`${company?.images?.["Company Pan Card"]}`);
      setCompanyFrontImage(`${company?.images?.["Office Front Image"]}`);
      setCompanyInsideImage(`${company?.images?.["Office Inside Image"]}`);
      setCompanyTeamImage(`${company?.images?.["Office Image with team"]}`);
    }
  }, [company, setValue]);
  const fileParts = userProfile?.user_profile_path?.split("/");
  const folderPath = fileParts?.slice(0, -1).join("/");
  const fileName =
    fileParts !== undefined ? fileParts[fileParts.length - 1] : "";
  const profilePicture = `${profilePath}${folderPath}/${encodeURIComponent(
    fileName
  )}`;

  const kycList = useMemo(() => {
    return kycData || [];
  }, [kycData]);

  const handleDocImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const uploadData = uploadDoc({
        file,
        folder: String(userId),
        doc_type_id: selectedKycType,
        user_id: user_id || "",
      });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      uploadPhoto({ file, folder: "profile" });
    }
  };

  return (
    <>
      <div className="mb-6 p-5 bg-white">
        <h1 className=" text-3xl font-extrabold">User </h1>
        <ProfileKYCForm />
      </div>

      <form
        action=""
        onSubmit={handleSubmit(onSubmit)}
        className="w-full bg-white p-6 border border-gray-200"
      >
        <h1 className=" text-3xl font-extrabold">Company</h1>

        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
            <div className="p-2 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg">
              <Shield className="w-3 h-3 text-white" />
            </div>
            <h3 className="text-[16px] font-semibold text-gray-800">
              KYC Details
            </h3>
          </div>
          {/* <div className="grid sm:grid-cols-2 items-end md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-[10px]  text-gray-700">
                Personal KYC Type <span className="text-red-500">*</span>
              </label>
              <select
                {...register("kyc_type", {
                  required: "KYC type is required",
                })}
                className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
              >
                <option value="">-- Select Kyc Document --</option>
                {kycList?.map((doc: any) => (
                  <option key={doc.doc_type_id} value={doc.doc_type_id}>
                    {doc.document_name}
                  </option>
                ))}
              </select>
              {errors.kyc_type && (
                <p className="text-[10px] text-red-500">
                  {errors.kyc_type.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-[10px]  text-gray-700">
                KYC Number <span className="text-red-500">*</span>
              </label>
              <input
                {...register("kyc")}
                className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                placeholder="Enter KYC number"
              />
              {errors.kyc && (
                <p className="text-[10px] text-red-500">{errors.kyc.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <button className="text-[10px] bg-blue-400 hover:bg-blue-500 cursor-pointer text-white px-5 py-2 rounded font-[500]">
                Verify
              </button>
            </div>

            <div className="space-y-2">
              <label className="block text-[10px]  text-gray-700">
                Last Updated On
              </label>
              <input
                type="date"
                {...register("last_updated_on")}
                className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
              />
              {errors.last_updated_on && (
                <p className="text-[10px] text-red-500">
                  {errors.last_updated_on.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-[10px]  text-gray-700">
                Verified By
              </label>
              <input
                placeholder="Enter Verified By"
                {...register("verified_by")}
                className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
              />
              {errors.verified_by && (
                <p className="text-[10px] text-red-500">
                  {errors.verified_by.message}
                </p>
              )}
            </div>

            {selectedKycType && (
              <div className="col-span-2">
                <label className="block text-[10px] ">KYC Upload</label>
                <input
                  type="file"
                  className="block w-full text-[10px] mt-1"
                  onChange={handleDocImageChange}
                />
              </div>
            )}
          </div> */}
        </div>

        <hr className="my-4 border-t border-gray-200" />

        <div className="flex gap-4">
          <div className="flex  w-[70%] gap-6">
            <div className="space-y-2">
              <label className="block text-[10px]  text-gray-700">
                PAN Number
              </label>
              <input
                {...register("pan")}
                className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                placeholder="Enter PAN number"
              />
              {errors.pan && (
                <p className="text-[10px] text-red-500">{errors.pan.message}</p>
              )}
            </div>

            <div className="space-y-2 mt-5">
              <button className="text-[10px] bg-blue-400 hover:bg-blue-500 cursor-pointer text-white px-5 py-2 rounded font-[500]">
                Verify
              </button>
            </div>
          </div>
          <div className="img-upload w-[30%] px-9">
            {/* <div className="flex flex-col items-end gap-6">
              <div className="flex flex-col items-center space-y-3">
                <div className="relative group">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={handleImageChange}
                      className="hidden"
                      accept="image/*"
                    />
                    <div className="relative">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          className="w-40 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                          alt="PAN Proof"
                        />
                      ) : (
                        <div className="w-25 h-25 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                          <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                        <Camera className="w-3 h-3" />
                      </div>
                    </div>
                  </label>
                </div>
                <div className="text-center">
                  <p className="text-[10px]  text-gray-700">PAN Proof</p>
                  <p className="text-xs text-gray-500">
                    Click to upload or change
                  </p>
                </div>
              </div>
            </div> */}
            <ImageEditor
              image={panProof}
              setImage={setPanProof}
              docTypeId={24}
              label="PAN Proof"
            />
          </div>
        </div>

        <hr className="my-4 border-t border-gray-200" />

        <div className="flex">
          <div className="flex w-[70%] gap-6">
            <div className="space-y-2">
              <label className="block text-[10px]  text-gray-700">GST</label>
              <input
                {...register("gst")}
                className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                placeholder="Enter GST number"
              />
              {errors.gst && (
                <p className="text-[10px] text-red-500">{errors.gst.message}</p>
              )}
            </div>

            <div className="space-y-2 mt-6">
              <button className="text-[10px] bg-blue-400 hover:bg-blue-500 cursor-pointer text-white px-5 py-2 rounded font-[500]">
                Verify
              </button>
            </div>
          </div>
          <div className="img-upload w-[30%] px-9">
            {/* <div className="flex flex-col items-end gap-6">
              <div className="flex flex-col items-center space-y-3">
                <div className="relative group">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={handleImageChange}
                      className="hidden"
                      accept="image/*"
                    />
                    <div className="relative">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          className="w-40 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                          alt="GST Proof"
                        />
                      ) : (
                        <div className="w-25 h-25 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                          <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                        <Camera className="w-3 h-3" />
                      </div>
                    </div>
                  </label>
                </div>
                <div className="text-center">
                  <p className="text-[10px]  text-gray-700">GST Proof</p>
                  <p className="text-xs text-gray-500">
                    Click to upload or change
                  </p>
                </div>
              </div>
            </div> */}
            <ImageEditor
              image={gstProof}
              setImage={setGstProof}
              docTypeId={21}
              label="GST Proof"
            />
          </div>
        </div>

        <hr className="my-4 border-t border-gray-200" />

        <div className="flex">
          <div className="flex w-[70%] gap-6">
            <div className="space-y-2">
              <label className="block text-[10px]  text-gray-700">MSME</label>
              <input
                {...register("msme")}
                className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                placeholder="Enter MSME number"
              />
              {errors.msme && (
                <p className="text-[10px] text-red-500">
                  {errors.msme.message}
                </p>
              )}
            </div>

            <div className="space-y-2 mt-6">
              <button className="text-[10px] bg-blue-400 hover:bg-blue-500 cursor-pointer text-white px-5 py-2 rounded font-[500]">
                Verify
              </button>
            </div>
          </div>
          <div className="img-upload w-[30%] px-9">
            {/* <div className="flex flex-col items-end gap-6">
              <div className="flex flex-col items-center space-y-3">
                <div className="relative group">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={handleImageChange}
                      className="hidden"
                      accept="image/*"
                    />
                    <div className="relative">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          className="w-40 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                          alt="MSME Proof"
                        />
                      ) : (
                        <div className="w-25 h-25 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                          <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                        <Camera className="w-3 h-3" />
                      </div>
                    </div>
                  </label>
                </div>
                <div className="text-center">
                  <p className="text-[10px]  text-gray-700">MSME Proof</p>
                  <p className="text-xs text-gray-500">
                    Click to upload or change
                  </p>
                </div>
              </div>
            </div> */}
            <ImageEditor
              image={msmeProof}
              setImage={setMsmeProof}
              docTypeId={32}
              label="MSME Proof"
            />
          </div>
        </div>

        <div className="flex gap-5 justify-between">
          <div className="w-[70%]">
            {/* Address Information */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                <div className="p-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg">
                  <MapPin className="w-3 h-3 text-white" />
                </div>
                <h3 className="text-[16px] font-semibold text-gray-800">
                  Address Proof
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-[10px]  text-gray-700">
                    Address
                  </label>
                  <input
                    {...register("address")}
                    className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                  />
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-[10px]  text-gray-700">
                      State <span className="text-red-500">*</span>
                    </label>
                    <div className="py-2 px-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-sm border border-indigo-200 text-gray-700 text-[10px]">
                      {userProfile?.state_name}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px]  text-gray-700">
                    Pin Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("pincode")}
                    className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                    placeholder="Enter pin code"
                  />
                </div>

                {/* <div className="space-y-2">
                  <label className="block text-[10px]  text-gray-700">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    // {...register("city")}
                    type="text"
                    disabled
                    placeholder="Type City Name"
                    onBlur={() =>
                      setTimeout(() => setShowSuggestions(false), 200)
                    }
                    onChange={handleCityInputChange}
                    value={cityInput || userProfile?.city_name}
                    className="px-3 py-2 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-md border border-indigo-200 text-gray-700 text-[10px]"
                  />
                </div> */}
              </div>
            </div>
          </div>

          <div className="img-upload w-[30%] px-9 py-12">
            {/* <div className="flex flex-col items-end gap-6">
              <div className="flex flex-col items-center space-y-3">
                <div className="relative group">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={handleImageChange}
                      className="hidden"
                      accept="image/*"
                    />
                    <div className="relative">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          className="w-40 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                          alt="Address Proof"
                        />
                      ) : (
                        <div className="w-25 h-25 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                          <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                        <Camera className="w-3 h-3" />
                      </div>
                    </div>
                  </label>
                </div>
                <div className="text-center">
                  <p className="text-[10px]  text-gray-700">Address Proof</p>
                  <p className="text-xs text-gray-500">
                    Click to upload or change
                  </p>
                </div>
              </div>
            </div> */}
            <ImageEditor
              image={addressProof}
              setImage={setAddressProof}
              docTypeId={2}
              label="Address Proof"
            />
          </div>
        </div>

        {/* License Details */}

        {/* <div className="flex justify-between">
          <div className="bg-white/70 backdrop-blur-sm border border-white/20 overflow-hidden w-[65%] sm:w-[70%]">
            <div className="space-y-8">
              <div className="space-y-3">
                <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg">
                    <IdCard className="w-3 h-3 text-white" />
                  </div>
                  <h3 className="text-[16px] font-semibold text-gray-800">
                    License Detail
                  </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="block text-[10px] font-medium text-gray-700">
                      Badge/License No. <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register("license_no")}
                      className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                    />
                    {errors.license_no && (
                      <p className="text-[10px] text-red-500">
                        {errors.license_no.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-medium text-gray-700">
                      License State <span className="text-red-600">*</span>
                    </label>
                    <input
                      {...register("license_state")}
                      placeholder="Select State"
                      className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                    />
                    {errors.license_state && (
                      <p className="text-[10px] text-red-500">
                        {errors.license_state.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-medium text-gray-700">
                      License Validity <span className="text-red-600">*</span>
                    </label>
                    <input
                      {...register("license_validity")}
                      value={userProfile?.license_validity}
                      disabled
                      className="border border-gray-300 cursor-not-allowed p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-medium text-gray-700">
                      GPS Device No
                    </label>
                    <input
                      {...register("gps_device_no")}
                      className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="img-upload px-9 py-12 w-[15%] sm:w-[20%]">
            <div className="flex flex-col items-end gap-6">
              <div className="flex flex-col items-center space-y-3">
                <div className="relative group">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={handleImageChange}
                      className="hidden"
                      accept="image/*"
                    />
                    <div className="relative">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          className="w-30 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                          alt="License Proof"
                        />
                      ) : (
                        <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                          <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                        <Camera className="w-4 h-4" />
                      </div>
                    </div>
                  </label>
                </div>
                <div className="text-center">
                  <p className="text-[10px] font-medium text-gray-700">
                    License Proof
                  </p>
                  <p className="text-xs text-gray-500">
                    Click to upload or change
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        <hr className="my-4 border-t border-gray-200" />

        <div className="flex items-center justify-evenly">
          <ImageEditor
            image={companyFrontImage}
            setImage={setCompanyFrontImage}
            docTypeId={33}
            label="Company Front Image"
          />
          <ImageEditor
            image={companyInsideImage}
            setImage={setCompanyInsideImage}
            docTypeId={34}
            label="Company Inside Image"
          />
          <ImageEditor
            image={companyTeamImage}
            setImage={setCompanyTeamImage}
            docTypeId={35}
            label="Company Team Image"
          />
        </div>
        {/* 
        <div className="flex">
          <div className="flex w-[70%] gap-6">
            <div className="flex flex-col gap-4">
              <div className="space-y-2">
                <label className="block text-[10px]  text-gray-700">RC</label>
                <input
                  {...register("rc")}
                  className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                  placeholder="Enter RC Details"
                />
                {errors.rc && (
                  <p className="text-[10px] text-red-500">
                    {errors.rc.message}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] text-gray-700">
                  GPS Device No
                </label>
                <input
                  {...register("gps_device_no")}
                  className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                />
              </div>
            </div>

            <div className="space-y-2 mt-6">
              <button className="text-[10px] bg-blue-400 hover:bg-blue-500 cursor-pointer text-white px-5 py-2 rounded font-[500]">
                Verify
              </button>
            </div>
          </div>

          <div className="img-upload w-[30%] px-9">
            <div className="flex flex-col items-end gap-6">
              <div className="flex flex-col items-center space-y-3">
                <div className="relative group">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={handleImageChange}
                      className="hidden"
                      accept="image/*"
                    />
                    <div className="relative">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          className="w-40 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                          alt="RC Proof"
                        />
                      ) : (
                        <div className="w-25 h-25 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                          <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                        <Camera className="w-3 h-3" />
                      </div>
                    </div>
                  </label>
                </div>
                <div className="text-center">
                  <p className="text-[10px]  text-gray-700">RC Proof</p>
                  <p className="text-xs text-gray-500">
                    Click to upload or change
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        <div className="flex justify-end gap-4 pt-10">
          <button
            type="button"
            className="py-1 px-3 border border-gray-300 rounded-md shadow-sm text-[10px] font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 cursor-pointer"
          >
            Reset
          </button>
          <button
            type="submit"
            className="py-1 px-3 cursor-pointer border border-transparent rounded-md shadow-md text-[10px] font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
          >
            Save
          </button>
        </div>

        {/* <KYCListTable /> */}
      </form>
    </>
  );
}
